import { ReactNode } from 'react'
import { Tabs } from 'antd'
import { Trans, useTranslation } from 'react-i18next'
import { Redirect, useHistory, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { GuideLink } from 'components/reusables/formInfoAlert'
import { DC3_CONFIGURATION_GUIDE_URL } from 'config/dc3'
import { PathRoutes } from 'enum/routes'
import { IMainState } from 'store/states'
import LegalAttestations from '../legalAttestations'
import {
  CertificateTemplateTab,
  CERTIFICATE_TEMPLATE_TAB_PATHS,
  isCertificateTemplateTabSlug,
} from './certificateTemplatesTabRoutes'
import { Page, PageTitle, TabsChrome, TabDescription, TabPanel } from './styled'

export { CertificateTemplateTab } from './certificateTemplatesTabRoutes'

const { TabPane } = Tabs

interface IProps {
  children: ReactNode
  inSecondaryView: boolean
  onLegalSecondaryViewChange?: (isSecondary: boolean) => void
}

const CertificateTemplatesTabs = ({
  children,
  inSecondaryView,
  onLegalSecondaryViewChange,
}: IProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { tab: tabFromUrl } = useParams<{ tab: string }>()
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )
  const settingsLoaded = useSelector(
    ({ general }: IMainState) => general.settingsLoaded
  )
  const translatePrefix = 'administration.certificate.templatesPage'

  const standardTabContent = (
    <TabPanel>
      {!inSecondaryView && (
        <TabDescription>
          {t(`${translatePrefix}.tabs.standard.description`)}
        </TabDescription>
      )}
      {children}
    </TabPanel>
  )

  const handleTabChange = (nextTab: string) => {
    if (!isCertificateTemplateTabSlug(nextTab)) return
    history.push(CERTIFICATE_TEMPLATE_TAB_PATHS[nextTab])
  }

  if (!isCertificateTemplateTabSlug(tabFromUrl)) {
    return <Redirect to={PathRoutes.CERTIFICATE_TEMPLATES_STANDARD} />
  }

  if (!settingsLoaded) {
    if (tabFromUrl === CertificateTemplateTab.legal) {
      return null
    }

    return <Page $hideChrome>{standardTabContent}</Page>
  }

  if (tabFromUrl === CertificateTemplateTab.legal && !attestationsEnabled) {
    return <Redirect to={PathRoutes.CERTIFICATE_TEMPLATES_STANDARD} />
  }

  if (!attestationsEnabled) {
    return <Page $hideChrome>{standardTabContent}</Page>
  }

  return (
    <Page $hideChrome={inSecondaryView}>
      <PageTitle $hidden={inSecondaryView}>
        {t(`${translatePrefix}.title`)}
      </PageTitle>
      <TabsChrome $hideChrome={inSecondaryView}>
        <Tabs activeKey={tabFromUrl} onChange={handleTabChange}>
          <TabPane
            tab={t(`${translatePrefix}.tabs.standard.title`)}
            key={CertificateTemplateTab.standard}
          >
            {standardTabContent}
          </TabPane>
          <TabPane
            tab={t(`${translatePrefix}.tabs.legal.title`)}
            key={CertificateTemplateTab.legal}
          >
            <TabPanel>
              {!inSecondaryView && (
                <TabDescription>
                  <Trans
                    i18nKey={`${translatePrefix}.tabs.legal.description`}
                    components={{
                      guideLink: (
                        <GuideLink
                          href={DC3_CONFIGURATION_GUIDE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  />
                </TabDescription>
              )}
              <LegalAttestations
                onSecondaryViewChange={onLegalSecondaryViewChange}
              />
            </TabPanel>
          </TabPane>
        </Tabs>
      </TabsChrome>
    </Page>
  )
}

export default CertificateTemplatesTabs
